/**
 * Utility functions for product management
 */

/**
 * Genera un identificatore composto combinando codice interno, taglia e colore
 * @param internal_code - Codice interno del prodotto
 * @param size - Taglia del prodotto (opzionale)
 * @param color - Colore del prodotto (opzionale)
 * @returns Identificatore composto nel formato:
 *   - Solo codice: "ABC123"
 *   - Con taglia: "ABC123-L"
 *   - Con colore: "ABC123--BLU"
 *   - Con entrambi: "ABC123-L-BLU"
 */
export const generateCompositeIdentifier = (
  internal_code: string,
  size?: string | null,
  color?: string | null
): string => {
  const sizePart = (size || '').trim();
  const colorPart = (color || '').trim();

  if (!sizePart && !colorPart) {
    return internal_code; // ABC123
  } else if (sizePart && !colorPart) {
    return `${internal_code}-${sizePart}`; // ABC123-L
  } else if (!sizePart && colorPart) {
    return `${internal_code}--${colorPart}`; // ABC123--BLU
  } else {
    return `${internal_code}-${sizePart}-${colorPart}`; // ABC123-L-BLU
  }
};

/**
 * Genera un display name completo per un prodotto con identificatore composto e descrizione
 * @param internal_code - Codice interno del prodotto
 * @param size - Taglia del prodotto (opzionale)
 * @param color - Colore del prodotto (opzionale)
 * @param description - Descrizione del prodotto
 * @returns Nome display completo nel formato "ABC123-L-BLU - Descrizione Prodotto"
 */
export const generateProductDisplayName = (
  internal_code: string,
  size?: string | null,
  color?: string | null,
  description?: string
): string => {
  const compositeId = generateCompositeIdentifier(internal_code, size, color);
  return description ? `${compositeId} - ${description}` : compositeId;
};

/**
 * Etichetta completa di un prodotto nei form/dettagli: "CODICE-TAGLIA-COLORE - CODICE CLIENTE - DESCRIZIONE"
 * (stesso formato della funzione locale di CustomerOrderForm; usata dai form/modal dei preventivi).
 * Taglia/colore assenti lasciano il separatore ("ABC123--BLU", "ABC123-L-").
 */
export const formatProductDisplay = (product: {
  internal_product_code?: string | null;
  size?: string | null;
  color?: string | null;
  client_product_code?: string | null;
  product_description?: string | null;
} | null | undefined): string => {
  if (!product) return 'N/A';
  let display = product.internal_product_code || '';
  display += product.size ? `-${product.size}` : '-';
  display += product.color ? `-${product.color}` : '-';
  display += ' - ';
  if (product.client_product_code) {
    display += product.client_product_code;
  }
  display += ` - ${product.product_description || ''}`;
  return display;
};